/**
 * Serial Profile API Service
 * Service for managing Serial Profile
 */

import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { 
  SerialProfile, 
  SerialProfileStatus
} from '@app/feature/configuration/product/serial-profile/serial-profile.component';

// API imports
import { SerialProfileManagementService } from '@app/api/stock-service/api/serialProfileManagement.service';
import { SerialProfileInfoVO } from '@app/api/stock-service/model/serialProfileInfoVO';
import { SerialProfileSaveRequestDTO } from '@app/api/stock-service/model/serialProfileSaveRequestDTO';
import { SerialProfileStatusUpdateRequestDTO } from '@app/api/stock-service/model/serialProfileStatusUpdateRequestDTO';
import { SerialProfileBySpusResponseDTO } from '@app/api/stock-service/model/serialProfileBySpusResponseDTO';

@Injectable({
  providedIn: 'root',
})
export class SerialProfileApiService {
  private readonly serialProfileManagementService = inject(SerialProfileManagementService);

  private extractProfileList(payload: unknown): SerialProfileInfoVO[] {
    if (Array.isArray(payload)) {
      return payload as SerialProfileInfoVO[];
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const container = payload as Record<string, unknown>;
    const directCandidates = ['data', 'content', 'items', 'results'];

    for (const key of directCandidates) {
      const value = container[key];
      if (Array.isArray(value)) {
        return value as SerialProfileInfoVO[];
      }
    }

    const nestedData = container['data'];
    if (nestedData && typeof nestedData === 'object') {
      const nested = nestedData as Record<string, unknown>;
      for (const key of ['content', 'items', 'results']) {
        const value = nested[key];
        if (Array.isArray(value)) {
          return value as SerialProfileInfoVO[];
        }
      }
    }

    return [];
  }

  /**
   * Map SerialProfileInfoVO to SerialProfile
   */
  private mapToSerialProfile(vo: SerialProfileInfoVO): SerialProfile {
    const validStatuses: SerialProfileStatus[] = ['ACTIVE', 'INACTIVE', 'ARCHIVED'];
    const status = vo.status && validStatuses.includes(vo.status as SerialProfileStatus)
      ? (vo.status as SerialProfileStatus)
      : 'ACTIVE';

    return {
      id: vo.id ?? 0,
      name: vo.name ?? '',
      description: vo.description ?? undefined,
      status,
      serialRequired: vo.serialRequired ?? false,
      lotRequired: vo.lotRequired ?? false,
      expiryRequired: vo.expiryRequired ?? false,
      creationDate: vo.creationDate,
    };
  }

  /**
   * Map SerialProfile to SerialProfileSaveRequestDTO
   */
  private mapToSaveRequestDTO(profile: Omit<SerialProfile, 'id'> | Partial<SerialProfile>): SerialProfileSaveRequestDTO {
    return {
      name: profile.name || '',
      description: profile.description,
      serialRequired: profile.serialRequired || false,
      lotRequired: profile.lotRequired || false,
      expiryRequired: profile.expiryRequired || false,
    };
  }

  /**
   * Get all Serial Profiles
   * Note: Component handles filtering and pagination client-side (like Tag Management)
   * @param filterStatus Optional status filter (e.g., 'ACTIVE' to get only active profiles)
   * @returns Observable with data array and totalRecords
   */
  getSerialProfiles(
    filterStatus?: SerialProfileStatus
  ): Observable<{
    data: SerialProfile[];
    totalRecords: number;
  }> {
    return this.serialProfileManagementService.listProfiles().pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return new Observable<SerialProfileInfoVO[]>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const jsonData = JSON.parse(reader.result as string);
                observer.next(jsonData);
                observer.complete();
              } catch (error) {
                console.error('Error parsing Blob to JSON:', error);
                observer.error(error);
              }
            };
            reader.onerror = (error) => {
              console.error('FileReader error:', error);
              observer.error(error);
            };
            reader.readAsText(response);
          });
        }
        return of(response);
      }),
      map((profilesPayload: unknown) => {
        const profiles = this.extractProfileList(profilesPayload);
        if (profiles.length === 0) {
          return { data: [], totalRecords: 0 };
        }
        
        let filteredProfiles = profiles;
        if (filterStatus) {
          filteredProfiles = profiles.filter(p => p.status === filterStatus);
        }
        
        const mappedProfiles = filteredProfiles.map(p => this.mapToSerialProfile(p));
        return {
          data: mappedProfiles,
          totalRecords: mappedProfiles.length,
        };
      }),
      catchError((error) => {
        console.error('Error fetching serial profiles:', error);
        return of({ data: [], totalRecords: 0 });
      })
    );
  }

  /**
   * Get all Serial Policies for dropdown selection
   * Returns only ACTIVE profiles
   * @deprecated Use getSerialProfiles('ACTIVE') instead
   */
  getAllSerialPolicy(): Observable<SerialProfile[]> {
    return this.getSerialProfiles('ACTIVE').pipe(
      map((response) => response.data)
    );
  }

  /**
   * Get Serial Profile by ID
   */
  getSerialProfileById(id: number): Observable<SerialProfile | null> {
    return this.serialProfileManagementService.getProfileById(id).pipe(
      map((vo: SerialProfileInfoVO) => this.mapToSerialProfile(vo)),
      catchError((error) => {
        console.error('Error fetching serial profile by id:', error);
        return of(null);
      })
    );
  }

  /**
   * Create Serial Profile
   */
  createSerialProfile(profile: Omit<SerialProfile, 'id'>): Observable<SerialProfile> {
    const requestDTO = this.mapToSaveRequestDTO(profile);
    
    return this.serialProfileManagementService.createProfile(requestDTO).pipe(
      map((vo: SerialProfileInfoVO) => this.mapToSerialProfile(vo)),
      catchError((error) => {
        console.error('Error creating serial profile:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update Serial Profile
   */
  updateSerialProfile(id: number, profile: Partial<SerialProfile>): Observable<SerialProfile> {
    const requestDTO = this.mapToSaveRequestDTO(profile);
    
    return this.serialProfileManagementService.updateProfile(id, requestDTO).pipe(
      map((vo: SerialProfileInfoVO) => this.mapToSerialProfile(vo)),
      catchError((error) => {
        console.error('Error updating serial profile:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update Serial Profile Status
   */
  updateSerialProfileStatus(id: number, status: SerialProfileStatus): Observable<SerialProfile> {
    const requestDTO: SerialProfileStatusUpdateRequestDTO = { status };
    
    return this.serialProfileManagementService.updateProfileStatus(id, requestDTO).pipe(
      map((vo: SerialProfileInfoVO) => this.mapToSerialProfile(vo)),
      catchError((error) => {
        console.error('Error updating serial profile status:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Assign profile to a single SPU
   * Wrapper for assignProfileToSpus
   */
  assignProfileToSpu(spuId: number, profileId: number): Observable<void> {
    return this.assignProfileToSpus(profileId, [spuId]);
  }


  /**
   * Get profiles by SPU IDs
   * Returns map of SPU ID to Serial Profile
   */
  getBySpuIds(spuIds: number[]): Observable<SerialProfileBySpusResponseDTO> {
    return this.serialProfileManagementService.getBySpuIds(spuIds).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Get profile assigned to SPU
   */
  getProfileBySpu(spuId: number): Observable<SerialProfile | null> {
    return this.getBySpuIds([spuId]).pipe(
      map((response: SerialProfileBySpusResponseDTO) => {
        const profileId = response.spuProfileMap?.[spuId];
        if (profileId && response.profileMap?.[profileId]) {
          return this.mapToSerialProfile(response.profileMap[profileId]);
        }
        return null;
      }),
      catchError((error) => {
        return of(null);
      })
    );
  }

  /**
   * Assign profile to multiple SPUs
   */
  assignProfileToSpus(profileId: number, spuIds: number[]): Observable<void> {
    return this.serialProfileManagementService.assignProfileToSpus(profileId, spuIds).pipe(
      map(() => undefined),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Reassign SPU to a new profile
   */
  reassignSpuToProfile(profileId: number, spuId: number): Observable<SerialProfile> {
    return this.serialProfileManagementService.reassignSpuToProfile(profileId, spuId).pipe(
      map((vo: SerialProfileInfoVO) => this.mapToSerialProfile(vo)),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
